import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin } from "lucide-react";
import { IMAGES } from "@/lib/images";
import { scrollToId } from "@/lib/scroll";

const LINES = [
    { text: "Architectural precision" },
    { text: "for South Florida" },
    { text: "interiors.", italic: true },
];

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], [0, 90]);
    const yInset = useTransform(scrollYProgress, [0, 1], [0, -50]);

    return (
        <section ref={ref} className="relative overflow-hidden bg-bone">
            <div className="texture-draft absolute inset-0" aria-hidden="true" />
            <div
                className="absolute -right-40 -top-40 h-[540px] w-[540px] rounded-full bg-oak/10 blur-[120px]"
                aria-hidden="true"
            />

            <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-4 pb-20 pt-32 sm:px-6 md:pt-40 lg:grid-cols-12 lg:gap-10 lg:px-8 lg:pb-28">
                <div className="lg:col-span-6">
                    <motion.p
                        data-testid="hero-eyebrow"
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.1 }}
                        className="flex items-center gap-3 font-mono text-[11px] font-semibold uppercase tracking-[0.24em] text-oak"
                    >
                        <span className="inline-block h-px w-10 bg-oak" />
                        Broward &amp; Palm Beach County Specialists
                    </motion.p>

                    <h1
                        data-testid="hero-title"
                        className="mt-8 font-serif text-5xl font-light leading-[1.04] tracking-tight text-espresso sm:text-6xl lg:text-7xl"
                    >
                        {LINES.map((line, i) => (
                            <span key={i} className="block overflow-hidden pb-1">
                                <motion.span
                                    className={`block ${line.italic ? "italic text-oak" : ""}`}
                                    initial={{ y: "112%" }}
                                    animate={{ y: 0 }}
                                    transition={{
                                        duration: 1.1,
                                        delay: 0.2 + i * 0.14,
                                        ease: [0.22, 1, 0.36, 1],
                                    }}
                                >
                                    {line.text}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.7 }}
                        className="mt-7 max-w-lg text-base leading-relaxed text-clay sm:text-lg"
                    >
                        Premier drywall, specialty paint, custom tile, and hardwood
                        flooring — built with uncompromising craftsmanship for the
                        homes of Broward &amp; Palm Beach County.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.85 }}
                        className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
                    >
                        <motion.button
                            data-testid="hero-inquiry-button"
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.97 }}
                            onClick={() => scrollToId("inquiry")}
                            className="group inline-flex items-center justify-center gap-3 bg-oak px-8 py-4 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-bone shadow-lift transition-colors hover:bg-oakdark"
                        >
                            Start Project Inquiry
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </motion.button>
                        <motion.button
                            data-testid="hero-portfolio-button"
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.97 }}
                            onClick={() => scrollToId("portfolio")}
                            className="inline-flex items-center justify-center gap-3 border border-espresso/20 px-8 py-4 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-espresso transition-colors hover:border-espresso hover:bg-espresso hover:text-bone"
                        >
                            Explore Recent Work
                        </motion.button>
                    </motion.div>

                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 1.05 }}
                        className="mt-10 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.22em] text-fog"
                    >
                        <MapPin className="h-3.5 w-3.5 text-oak" />
                        Drywall · Paint · Tile &amp; Marble · Flooring — Free Estimates
                    </motion.p>
                </div>

                <div className="relative lg:col-span-6">
                    <motion.div
                        initial={{ clipPath: "inset(0 0 100% 0)" }}
                        animate={{ clipPath: "inset(0 0 0% 0)" }}
                        transition={{ duration: 1.3, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
                        className="relative ml-auto aspect-[4/3] w-full max-w-[620px] overflow-hidden shadow-frame lg:aspect-[5/6] lg:max-w-none"
                    >
                        <motion.img
                            data-testid="hero-image"
                            src={IMAGES.heroMain}
                            alt="Bright South Florida living room with warm wood floors and white walls finished by Steevens Complete Interiors"
                            className="h-full w-full scale-110 object-cover"
                            style={{ y }}
                            loading="eager"
                        />
                        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-espresso/25 via-transparent to-transparent" />
                    </motion.div>

                    <motion.div
                        style={{ y: yInset }}
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 1 }}
                        className="absolute -bottom-8 left-2 hidden w-52 border-8 border-bone shadow-frame sm:block lg:-left-6"
                    >
                        <img
                            src={IMAGES.heroInset}
                            alt="Shelves of ceramic tile samples in different colors"
                            className="aspect-square w-full object-cover"
                            loading="eager"
                        />
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8, delay: 1.2 }}
                        className="absolute -top-5 right-4 bg-espresso px-5 py-4 text-bone shadow-frame lg:right-8"
                    >
                        <p className="font-serif text-lg italic leading-tight">Serving the</p>
                        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-wood">
                            Broward Line to the
                        </p>
                        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-wood">
                            Palm Beaches
                        </p>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};
